// src/portal/PortalReviewPage.jsx
//
// One detail page, two readings, chosen by `viewer_role` from route 6.
//
// ── WHY ONE PAGE AND NOT TWO ────────────────────────────────────────────────
//
// The server already answers the question. shapePlanReviewDetail returns
// viewer_role and it is the only place the client is told which side it is on.
// Two pages would fetch the same review twice, mount the same map twice, poll
// the same thread twice, and each would have to agree about the role
// independently -- a second place to get it wrong, for no second behaviour.
//
// ── TWO MAPS, ONE AT A TIME ─────────────────────────────────────────────────
//
// Chosen by viewer_role, which means only ONE is ever mounted. That is what
// satisfies MlDraw's single-map constraint by construction rather than by care:
// its source and layer ids are constant strings, so two instances on one map
// collide -- two instances on two maps do not, and this renders one map.
import { useEffect, useState } from "react";
import { getPlanReview, exportRevision, PortalError } from "../api/portalApi";
import PortalThread from "./PortalThread";
import PortalUploadPanel from "./PortalUploadPanel";
import PortalSubmitterMap from "./PortalSubmitterMap";
import PortalReviewerMap from "./PortalReviewerMap";
import PortalFindingForm from "./PortalFindingForm";
import PortalMapPanel from "./PortalMapPanel";
import PortalDecisionPanel from "./PortalDecisionPanel";
import PortalFindingsPanel from "./PortalFindingsPanel";
import { findingCountFor } from "./portalMarkup";
import PortalPlanHead from "./PortalPlanHead";
import PortalRevisionRail from "./PortalRevisionRail";
import { listPlanReviews } from "../api/portalApi";

// STATUS_SENTENCE lives in portalStatus.js now — PortalPlanHead needs it too,
// and importing it from here would close a cycle. Re-exported so the assertion
// that it covers exactly the real statuses keeps importing it from the page it
// is about.
export { STATUS_SENTENCE } from "./portalStatus";

export default function PortalReviewPage({ planReviewId }) {
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  // Bumped after a successful submission so the effect re-reads route 6. The
  // create response is a success signal, not a data source — portalApi.js says
  // so for route 9 and it holds here: route 4's 201 does not carry the shaped
  // review, and rendering from it would put a second shape on screen.
  const [reloads, setReloads] = useState(0);
  // ONE SELECTION, TWO VIEWS — the feature list and the map, exactly as on the
  // submitter side. useFeatureSelection is the single copy of the wiring.
  const [selected, setSelected] = useState(null);
  // The reviewer's own annotation, drawn on the map and not yet attached to a
  // finding. Held here because the MAP produces it and the FORM sends it, and
  // neither owns the other.
  const [drawnGeom, setDrawnGeom] = useState(null);
  // ── TELLING THE MAP THE GEOMETRY IS SPENT ────────────────────────────────
  //
  // The page learns the write succeeded; the MAP owns MlDraw. Until this, only
  // the page's own copy was cleared, so the shape stayed on the map and could
  // be attached to a SECOND finding without the reviewer drawing anything —
  // REM-383.
  //
  // A COUNTER, NOT A BOOLEAN. "That geometry is spent" is an EVENT, and a
  // boolean cannot express it twice: two findings in a row would set `true`
  // twice and the map would see no change on the second. Bumping is the same
  // shape as useCaseBuilderStore's `zoomNonce`
  // (CaseBuilderMap.jsx:195-205) — the tree's existing answer to "the same
  // command, issued again".
  //
  // AND NOT `drawnGeom` FED BACK DOWN. The map PRODUCES that value through
  // onDraw; passing it back as a prop would close a loop between the two, and
  // a one-way "this happened" signal has no such edge.
  const [drawnConsumed, setDrawnConsumed] = useState(0);
  // ── THE AWAITING-YOU QUEUE, FOR PREV/NEXT ────────────────────────────────
  //
  // Route 5 again. Stepping through "everything visible" would walk approved
  // plans nobody is waiting on; the queue is the set this caller may WRITE at,
  // and route 5 already derives it from mayWritePlanReview. Fetched here rather
  // than threaded from the index, because the detail route is linkable and is
  // routinely opened without the index having mounted at all.
  const [queue, setQueue] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await listPlanReviews();
        const st = list?.awaiting_statuses ?? [];
        if (alive) setQueue((list?.reviews ?? []).filter((r) => st.includes(r.status)));
      } catch {
        // A failed queue read costs prev/next and nothing else. The plan itself
        // is already on screen from route 6, and an error banner here would
        // report a navigation aid as a broken page.
        if (alive) setQueue([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getPlanReview(planReviewId);
        if (alive) { setReview(data); setError(null); }
      } catch (e) {
        if (alive) setError(e instanceof PortalError ? e : new PortalError(0, { error: String(e) }));
      }
    })();
    return () => { alive = false; };
  }, [planReviewId, reloads]);

  if (error) {
    return (
      <div className="p-6 text-sm">
        <p className="text-red-400">{error.message}</p>
        {error.code && <p className="mt-1 font-mono text-xs opacity-70">{error.code}</p>}
      </div>
    );
  }
  if (!review) return <div className="p-6 text-sm text-fg-secondary">Loading…</div>;

  const role = review.viewer_role;
  const current = review.revisions?.find((r) => r.revision === review.current_revision) ?? null;

  async function onExport() {
    try {
      const { blob, filename } = await exportRevision(planReviewId, current.revision);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // export_not_audited is a 500 that MEANS RETRY (§3.14). Generic 500
      // handling would tell the user the export failed permanently, which is
      // the opposite of true.
      setError(e);
    }
  }

  return (
    <div className="op-portal-surface flex min-h-0 flex-1 flex-col gap-5 overflow-auto p-6">
        {/* PORTED: the mock's .plan-head. The case number, the counterparty and
            the status all moved INTO it — it renders them, so nothing is lost
            by them leaving here. */}
        <PortalPlanHead review={review} role={role} queue={queue} />

      {/* ── THE MOCK'S .review__body ─────────────────────────────────────────
          A two-column grid, the right one a fixed 380px, items aligned to the
          top. Column 1 is the plan: map panel, revision rail, decision.
          Column 2 is the correspondence: findings, then the conversation.

          THE FINDINGS PANEL WAS UNDER THE MAP and it should not have been. The
          left column is what the reviewer LOOKS at; the right is what they
          WRITE. Putting the findings under the map pushed the conversation off
          the first screen and made the two halves of a reply — the finding and
          the note — sit in different columns.

          The mock also collapses to one column at narrow widths; `lg:` is where
          380px + a usable map stops fitting. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-4">

        {/* THE UPLOAD. Gated on the SERVER'S answer, not on a status list.
            
            This first shipped as `["awaiting_submission", "returned"].includes(
            review.status)` — two states the stage table does not contain. The
            real ones are draft / submitted / changes_requested / approved, so
            the panel never rendered for any real review, and the test passed
            because its fixture used the invented status as well.

            `viewer_may_write` comes from mayWritePlanReview({status, role}) via
            route 6. A submitter may write at `draft` and at
            `changes_requested`, which is the same thing the wrong list was
            reaching for — but reached for by the one component that already
            knows, instead of restated by one that does not. */}
        {role === "submitter" && review.viewer_may_write && (
          <PortalUploadPanel
            planReviewId={review.id}
            onSubmitted={() => setReloads((n) => n + 1)}
          />
        )}

        {current ? (
          <section className="flex flex-col gap-2">
            {/* PORTED: the mock's .map-panel chrome — head, legend, source and
                the foot. The media box inside it is unchanged; MapLibre still
                owns everything it draws. */}
            <PortalMapPanel
              revision={current}
              features={review.features ?? []}
              imagery={review.imagery}
              onExport={onExport}
            >
              {role === "reviewer" ? (
                <PortalReviewerMap
                  features={review.features}
                  imagery={review.imagery}
                  markup={review.markup}
                  selectedOrdinal={selected}
                  onSelectFeature={setSelected}
                  onDraw={(f) => setDrawnGeom(f?.geometry ?? null)}
                  drawnConsumed={drawnConsumed}
                />
              ) : (
                <PortalSubmitterMap
                  features={review.features}
                  markup={review.markup}
                />
              )}
            </PortalMapPanel>

            {/* The same geometry in text, beside the map rather than instead of
                it. A submitter checking that ordinal 3 is the access road
                should not have to click a shape to find out. */}
            <ul className="flex flex-col gap-0.5 text-xs text-fg-secondary">
              {review.features?.map((f) => {
                const n = findingCountFor(review.markup, f.ordinal);
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      data-testid={`review-feature-${f.ordinal}`}
                      aria-pressed={selected === f.ordinal}
                      onClick={() => setSelected(f.ordinal)}
                      className={`w-full rounded px-1 text-left ${selected === f.ordinal ? "bg-bg-soft" : ""}`}
                    >
                      #{f.ordinal} · {f.submitter_tag} · {f.geom_class}
                      {f.label ? ` · ${f.label}` : ""}
                      {/* THE COUNT, NOT THREE OUTLINES. A shape carrying three findings
                          cannot show three styles: stacked styles are unreadable at any
                          zoom, and drawing only the newest hides the rest. The map
                          answers "how many"; selecting filters the list to "which". */}
                      {n > 0 && (
                        <span data-testid={`review-feature-count-${f.ordinal}`}
                              className="ml-1 rounded bg-bg-soft px-1 text-[10px]">
                          {n} finding{n === 1 ? "" : "s"}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* PORTED: the mock's .revisions rail, in column 1 beneath the
                map, where the mock puts it. */}
            <PortalRevisionRail
              revisions={review.revisions}
              markup={review.markup}
              currentRevision={review.current_revision}
              status={review.status}
            />

            {/* PORTED: the mock's .decision — the gate line and both buttons,
                with the primary/outline swap wired from the README's rule. The
                two controls keep their own logic and refusals; the panel
                decides only which one is solid. */}
            {role === "reviewer" && review.viewer_may_write && (
              <PortalDecisionPanel
                review={review}
                markup={review.markup ?? []}
                mayApprove={review.viewer_may_approve === true}
                onReturned={() => setReloads((n) => n + 1)}
                onApproved={() => setReloads((n) => n + 1)}
              />
            )}

          </section>
        ) : (
          <p className="text-xs text-fg-secondary">No revision has been submitted yet.</p>
        )}
        </div>

        {/* ── COLUMN 2: what the reviewer writes ──────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* PORTED: the mock's .findings — the privacy line and one card per
              finding, each with a numbered pin, the kind chip as its LITERAL
              enum value, the note and a target/author footer. */}
          {current && (
            <PortalFindingsPanel
              markup={review.markup ?? []}
              role={role}
              features={review.features ?? []}
            />
          )}

          {/* THE FORM sits under the findings, where the mock puts its
              "+ Add finding". Reviewer only, and only while the review is open
              for findings — the server decides for real (route 9 answers
              not_open_for_markup); this decides whether to show it. */}
          {current && role === "reviewer" && review.viewer_may_write && (
            <PortalFindingForm
              planReviewId={review.id}
              feature={review.features?.find((f) => f.ordinal === selected) ?? null}
              geom={drawnGeom}
              onWritten={() => {
                setDrawnGeom(null);
                // ONLY ON SUCCESS. PortalFindingForm calls onWritten from the
                // try-block after createMarkup resolves; every refusal takes
                // the catch and never reaches here, so a rejected finding
                // keeps the shape the reviewer drew. Asserted, not implied.
                setDrawnConsumed((n) => n + 1);
                setReloads((n) => n + 1);
              }}
            />
          )}

          <PortalThread
            planReviewId={planReviewId}
            viewerRole={role}
            disabled={review.status === "approved"}
            disabledReason="This review is approved. A closed review does not take new notes."
          />
        </div>
      </div>
    </div>
  );
}
